import React from "react";
import { Card } from "@shadcn-in-nx/ui/card";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shadcn-in-nx/ui/select";
import { Clock, User, Building, Tag, Archive } from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import TagInput from "./TagInput";

const statusColors = {
    OPEN: "bg-blue-100 text-blue-700",
    IN_PROGRESS: "bg-yellow-100 text-yellow-700",
    WAITING_ON_CUSTOMER: "bg-purple-100 text-purple-700",
    RESOLVED: "bg-green-100 text-green-700",
    CLOSED: "bg-gray-100 text-gray-700",
};

export default function TicketDetailHeader({ ticket, onUpdateStatus, onUpdatePriority, onUpdateTags, isUpdating, availableTags }) {
    const { t } = useTranslation();

    return (
        <Card className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs text-muted-foreground font-mono">#{ticket.id.slice(0, 8)}</span>
                        {ticket.archivedAt && (
                            <Badge variant="secondary" className="bg-gray-200 text-gray-600">
                                <Archive className="w-3 h-3 mr-1" />Archived
                            </Badge>
                        )}
                        <Badge className={statusColors[ticket.status]}>{ticket.status?.replace(/_/g, " ")}</Badge>
                    </div>
                    <h1 className="text-base sm:text-lg font-bold break-words">{ticket.subject}</h1>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <Select value={ticket.status} onValueChange={onUpdateStatus} disabled={isUpdating}>
                        <SelectTrigger className="w-[130px] sm:w-[160px] h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {["OPEN", "IN_PROGRESS", "WAITING_ON_CUSTOMER", "RESOLVED", "CLOSED"].map((s) => (
                                <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={ticket.priority} onValueChange={onUpdatePriority} disabled={isUpdating}>
                        <SelectTrigger className="w-[90px] sm:w-[100px] h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {["URGENT", "HIGH", "NORMAL", "LOW"].map((p) => (
                                <SelectItem key={p} value={p}>{p}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {ticket.company && <span className="flex items-center gap-1"><Building className="w-3 h-3" />{ticket.company.name}</span>}
                {ticket.createdBy && <span className="flex items-center gap-1"><User className="w-3 h-3" />{ticket.createdBy.firstName} {ticket.createdBy.lastName}</span>}
                {!ticket.createdBy && ticket.senderEmail && <span className="flex items-center gap-1"><User className="w-3 h-3" /><span className="truncate max-w-[200px]">{ticket.senderName || ticket.senderEmail}</span></span>}
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{format(new Date(ticket.createdAt), "MMM d, yyyy h:mm a")}</span>
            </div>
            <div className="mt-3 pt-3 border-t flex items-center gap-2">
                <Tag className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <TagInput tags={ticket.tags || []} availableTags={availableTags} onChange={onUpdateTags} disabled={isUpdating} />
            </div>
        </Card>
    );
}
